import { Component, OnInit } from '@angular/core';
import { ErrorService } from '../../../shared/services/error.service';
import { AuthService } from '../../../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  TrafficFineHistoryDto,
  TrafficFineInfractionInfo,
  TrafficFinesService
} from '../../../services/traffic-fines.service';
import { TrafficFineAccountsPayableSyncService } from '../../../services/traffic-fine-accounts-payable-sync.service';
import { AccountsReceivableService } from '../../../services/accounts-receivable.service';
import { AccountsReceivableCreate } from '../../../models/AccountsReceivable';
import {
  TrafficFineDriverIndication,
  TrafficFineDriverIndicationService
} from '../../../services/traffic-fine-driver-indication.service';
import {
  B2BTrafficFineCaseCommentDto,
  B2BTrafficFineCaseDto,
  B2BTrafficFineCaseEvidenceDto,
  B2BTrafficFineCasesService
} from '../../../services/b2b-traffic-fine-cases.service';
import { ContractsService } from '../../../services/contracts.service';
import { B2bContract } from '../../../models/Contract';
import {
  calculateTrafficFineChargeAmount,
  TRAFFIC_FINE_CHARGE_OPTIONS,
  TrafficFineChargeMultiplier
} from '../traffic-fines/traffic-fine-charge.util';
import { Observable, of, throwError, forkJoin } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';

export type TrafficFineHistoryTipo =
  | 'registro'
  | 'notificacao'
  | 'indicacao'
  | 'financeiro'
  | 'pagamento'
  | 'sistema'
  | 'alerta';

export interface TrafficFineHistoryEvent {
  id: number;
  date: string;
  description: string;
  status: string;
  tipo: TrafficFineHistoryTipo;
  usuario?: string;
  detalhes?: string;
}

interface DetailField {
  label: string;
  value: string;
  highlight?: boolean;
}

interface DetailSection {
  title: string;
  icon: string;
  fields: DetailField[];
}

interface TrafficFineDriverInfo {
  name: string;
  cpf: string;
  rg: string;
  address: string;
  city: string;
  state: string;
}

@Component({
  selector: 'app-traffic-fine-detail',
  templateUrl: './traffic-fine-detail.component.html',
  styleUrls: ['./traffic-fine-detail.component.css']
})
export class TrafficFineDetailComponent implements OnInit {
  id: number | null = null;
  fine: any | null = null;
  loading = false;
  error: string | null = null;
  // Status modal
  availableStatuses: string[] = [
    'Licenciamento',
    'Identificado',
    'Recurso',
    'Correio',
    'Assinado',
    'Não Assinado',
    'Indicação Direta',
    'Reembolsado'
  ];
  showStatusSelectionModal = false;
  statusModalLoading = false;
  statusModalError: string | null = null;
  statusModalSelectedStatus: string | null = null;

  history: TrafficFineHistoryEvent[] = [];
  historyLoading = false;
  historyError: string | null = null;

  readonly chargeOptions = TRAFFIC_FINE_CHARGE_OPTIONS;
  showChargeModal = false;
  chargeModalLoading = false;
  chargeModalSubmitting = false;
  chargeModalError: string | null = null;
  selectedChargeMultiplier: TrafficFineChargeMultiplier = 2;
  driverInfo: TrafficFineDriverInfo | null = null;
  /** Valor cobrado do cliente (à vista = infração; com acréscimo se definido). */
  clientAmountDraft: number | null = null;
  clientAmountBusy = false;

  indicationLimitAt: string | null = null;
  indicatedAt: string | null = null;
  indicatedDriver: TrafficFineDriverIndication | null = null;

  b2bCase: B2BTrafficFineCaseDto | null = null;
  b2bCaseLoading = false;
  b2bCaseBusy = false;
  b2bCaseError: string | null = null;
  /** Id numérico OU número do contrato (ex.: B2B-2026-804714) — fallback manual. */
  assignContractInput = '';
  assignContractPreview: string | null = null;
  /** Lista de contratos B2B ativos para indicação. */
  b2bActiveContracts: B2bContract[] = [];
  b2bContractsLoading = false;
  b2bContractsFilter = '';
  selectedAssignContractId: number | null = null;
  b2bStatusTarget = '';
  b2bComments: B2BTrafficFineCaseCommentDto[] = [];
  b2bCommentDraft = '';
  b2bCommentInternal = false;
  b2bEvidences: B2BTrafficFineCaseEvidenceDto[] = [];
  b2bEvidenceBusy = false;
  detailTab: 'dados' | 'chamado' | 'chat' | 'anexos' | 'email' = 'dados';
  /** Cache estável — getter no *ngFor + ngModel travava a tela (loop de CD). */
  detailSections: DetailSection[] = [];
  /** Quando aberto pelo espelho B2B (`/b2b/contracts/:id/multas/:fineId/view`). */
  private b2bContractIdContext: number | null = null;
  readonly b2bCaseStatuses = [
    'Aguardando_indicacao',
    'Indicacao_enviada',
    'Em_analise_ERP',
    'Contestando',
    'Aceita',
    'Cobranca',
    'Encerrada'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: TrafficFinesService,
    private errorService: ErrorService,
    private authService: AuthService,
    private syncService: TrafficFineAccountsPayableSyncService,
    private accountsReceivableService: AccountsReceivableService,
    private driverIndicationService: TrafficFineDriverIndicationService,
    private b2bCases: B2BTrafficFineCasesService,
    private contractsService: ContractsService
  ) {}

  /** Linha da listagem repassada na navegação (placa e cliente vêm daqui). */
  private listRowSnapshot: any | undefined;

  ngOnInit(): void {
    this.listRowSnapshot = this.readListRowSnapshot();
    this.route.paramMap.subscribe(params => {
      // Financeiro: /financial/traffic-fines/:id/view
      // B2B espelho: /b2b/contracts/:id/multas/:fineId/view
      const fineIdParam = params.get('fineId');
      const idParam = params.get('id');
      if (fineIdParam) {
        this.id = +fineIdParam;
        this.b2bContractIdContext = idParam ? +idParam : null;
        if (this.b2bContractIdContext && !this.assignContractInput.trim()) {
          this.assignContractInput = String(this.b2bContractIdContext);
        }
      } else {
        this.id = idParam ? +idParam : null;
        this.b2bContractIdContext = null;
      }
      if (this.id) this.load();
      else this.error = 'Registro não informado.';
    });
  }

  onChangeStatus(): void {
    if (this.isInfractionNotIndicable(this.infractionInfo.infractionStatus)) {
      this.errorService.showErrorMessage('Status "NIC Não Indicável" não permite alteração de status.');
      return;
    }
    this.statusModalSelectedStatus = this.infractionInfo.infractionStatus || null;
    this.statusModalError = null;
    this.showStatusSelectionModal = true;
  }

  isInfractionNotIndicable(status: string): boolean {
    if (!status) return false;
    try {
      const s = String(status).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[_\s-]+/g, '');
      return s === 'nicnaoindicavel' || s === 'nicnaoindicável' || s.includes('nic') && s.includes('indica');
    } catch {
      return false;
    }
  }

  closeStatusModal(): void {
    this.showStatusSelectionModal = false;
    this.statusModalLoading = false;
    this.statusModalSelectedStatus = null;
    this.statusModalError = null;
  }

  selectStatusFromModal(status: string): void {
    if (!this.id) {
      this.statusModalError = 'ID da multa não informado.';
      return;
    }
    this.statusModalLoading = true;
    this.statusModalError = null;
    const userName = this.authService.getUserNameFromToken() || this.authService.currentUserValue?.username || 'Sistema';
    // Use single canonical property name expected by backend to avoid duplicate column updates
    const payload: any = { InfractionStatus: status, UserName: userName };
    this.service.update(this.id, payload).subscribe({
      next: () => {
        this.statusModalLoading = false;
        this.showStatusSelectionModal = false;
        this.errorService.showSuccessMessage('Status alterado com sucesso.');
        this.load();
      },
      error: (err) => {
        this.statusModalLoading = false;
        this.statusModalError = this.errorService.formatHttpErrorForDisplay(err) || 'Erro ao alterar status.';
      }
    });
  }

  private readListRowSnapshot(): any | undefined {
    const historyState = history.state as { listRow?: any };
    if (historyState?.listRow) {
      return historyState.listRow;
    }
    const nav = this.router.lastSuccessfulNavigation ?? this.router.getCurrentNavigation();
    return nav?.extras?.state?.['listRow'];
  }

  load(): void {
    if (!this.id) return;
    this.loading = true;
    this.error = null;
    const userName =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      'Sistema';
    this.service
      .getById(this.id, this.listRowSnapshot)
      .pipe(
        switchMap(data =>
          this.syncService
            .syncPaymentStatus(data, userName, { persist: false })
            .pipe(catchError(() => of(data)))
        )
      )
      .subscribe({
        next: data => {
          this.fine = data;
          this.loading = false;
          this.syncClientAmountDraft();
          this.rebuildDetailSections();
          this.loadIndication();
          this.loadB2bCase();
          const existingContract = Number(
            this.pickRawDisplay(data, 'b2BContractId', 'B2BContractId', 'b2bContractId') || 0
          );
          if (existingContract > 0 && !this.assignContractInput.trim()) {
            this.assignContractInput = String(existingContract);
          }
        },
        error: err => {
          this.error = err?.message || 'Erro ao carregar multa';
          this.loading = false;
          this.detailSections = [];
        }
      });
  }

  loadB2bCase(): void {
    if (!this.id) return;
    this.b2bCaseLoading = true;
    this.b2bCaseError = null;
    this.b2bCases.getByFineId(this.id).subscribe({
      next: c => {
        this.b2bCase = c;
        this.b2bCaseLoading = false;
        if (c) {
          this.loadB2bComments();
          this.loadB2bEvidences();
        } else {
          this.b2bComments = [];
          this.b2bEvidences = [];
        }
        this.loadHistories();
      },
      error: () => {
        this.b2bCase = null;
        this.b2bCaseLoading = false;
        this.loadHistories();
      }
    });
  }

  loadB2bComments(): void {
    if (!this.b2bCase) return;
    this.b2bCases.getComments(this.b2bCase.id, this.b2bCase.b2BContractId).subscribe({
      next: list => (this.b2bComments = list || []),
      error: () => (this.b2bComments = [])
    });
  }

  loadB2bEvidences(): void {
    if (!this.b2bCase) {
      this.b2bEvidences = [];
      return;
    }
    this.b2bCases.getEvidences(this.b2bCase.id, this.b2bCase.b2BContractId).subscribe({
      next: list => (this.b2bEvidences = list || []),
      error: () => (this.b2bEvidences = [])
    });
  }

  selectDetailTab(tab: 'dados' | 'chamado' | 'chat' | 'anexos' | 'email'): void {
    this.detailTab = tab;
    if (tab === 'chamado' && !this.b2bCase) {
      this.loadActiveB2bContracts();
    }
    if (tab === 'chat') this.loadB2bComments();
    if (tab === 'anexos') this.loadB2bEvidences();
  }

  /** Contratos ativos (e similares) para o select de indicação B2B. */
  loadActiveB2bContracts(): void {
    if (this.b2bContractsLoading || this.b2bActiveContracts.length > 0) return;
    this.b2bContractsLoading = true;
    const pageSize = 100;
    this.contractsService.listB2bContracts(1, pageSize).subscribe({
      next: first => {
        const pages = Math.min(Math.max(first.pagination?.totalPages || 1, 1), 8);
        const rest$ =
          pages <= 1
            ? of([] as B2bContract[][])
            : forkJoin(
                Array.from({ length: pages - 1 }, (_, i) =>
                  this.contractsService.listB2bContracts(i + 2, pageSize).pipe(
                    map(r => r.data || []),
                    catchError(() => of([] as B2bContract[]))
                  )
                )
              );

        rest$.subscribe({
          next: extraPages => {
            const all = [...(first.data || []), ...extraPages.flat()];
            this.b2bActiveContracts = all
              .filter(c => this.isAssignableB2bStatus(c.status))
              .sort((a, b) =>
                (a.companyName || a.customerName || a.contractNumber || '').localeCompare(
                  b.companyName || b.customerName || b.contractNumber || '',
                  'pt-BR'
                )
              );
            this.b2bContractsLoading = false;
          },
          error: () => {
            this.b2bActiveContracts = (first.data || []).filter(c =>
              this.isAssignableB2bStatus(c.status)
            );
            this.b2bContractsLoading = false;
          }
        });
      },
      error: () => {
        this.b2bContractsLoading = false;
        this.b2bCaseError =
          this.b2bCaseError || 'Não foi possível carregar a lista de contratos B2B ativos.';
      }
    });
  }

  private isAssignableB2bStatus(status: string | null | undefined): boolean {
    const s = (status || '').trim().toLowerCase();
    if (!s) return true;
    if (['cancelled', 'canceled', 'cancelado', 'encerrado', 'inativo', 'inactive'].includes(s)) {
      return false;
    }
    // Preferência: ativos / vigentes; demais status “não cancelados” também entram na lista.
    return true;
  }

  get filteredB2bContractsForAssign(): B2bContract[] {
    const q = (this.b2bContractsFilter || '').trim().toLowerCase();
    if (!q) return this.b2bActiveContracts;
    return this.b2bActiveContracts.filter(c => {
      const blob = [
        c.contractNumber,
        c.contractName,
        c.companyName,
        c.customerName,
        c.cnpj,
        String(c.id)
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }

  b2bContractOptionLabel(c: B2bContract): string {
    const client = (c.companyName || c.customerName || 'Cliente').trim();
    const num = (c.contractNumber || `Id ${c.id}`).trim();
    const status = c.status ? ` · ${c.status}` : '';
    return `${client} — ${num}${status}`;
  }

  onAssignContractSelected(): void {
    if (!this.selectedAssignContractId) {
      this.assignContractInput = '';
      return;
    }
    this.assignContractInput = String(this.selectedAssignContractId);
  }

  onEvidenceFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !this.b2bCase) return;
    this.b2bEvidenceBusy = true;
    this.b2bCaseError = null;
    const actor =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      'Sistema';
    this.b2bCases.uploadEvidence(this.b2bCase.id, file, actor).subscribe({
      next: ev => {
        this.b2bEvidences = [ev, ...this.b2bEvidences];
        this.b2bEvidenceBusy = false;
        this.errorService.showSuccessMessage('Anexo enviado.');
        input.value = '';
      },
      error: err => {
        this.b2bEvidenceBusy = false;
        this.b2bCaseError =
          this.errorService.formatHttpErrorForDisplay(err) || 'Falha no upload do anexo.';
        input.value = '';
      }
    });
  }

  isEvidenceImage(e: B2BTrafficFineCaseEvidenceDto): boolean {
    return (e.mimeType || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(e.fileName || '');
  }

  formatEvidenceSize(bytes: number | null | undefined): string {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  assignB2bCase(): void {
    if (!this.id || !this.assignContractInput.trim()) return;
    this.b2bCaseBusy = true;
    this.b2bCaseError = null;
    this.assignContractPreview = null;
    const actor =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      'Sistema';

    this.resolveB2bContractId(this.assignContractInput.trim())
      .pipe(
        switchMap(resolved => {
          this.assignContractPreview = `${resolved.contractNumber} (Id ${resolved.id})`;
          return this.b2bCases.assign(this.id!, resolved.id, actor);
        })
      )
      .subscribe({
        next: c => {
          this.b2bCase = c;
          this.b2bCaseBusy = false;
          this.errorService.showSuccessMessage(
            `Multa atribuída ao contrato ${this.assignContractPreview || 'B2B'}.`
          );
          this.loadB2bComments();
          this.load();
        },
        error: err => {
          this.b2bCaseBusy = false;
          this.b2bCaseError =
            this.errorService.formatHttpErrorForDisplay(err) ||
            (typeof err?.message === 'string' ? err.message : null) ||
            'Erro ao atribuir multa. Confira o Id/número do contrato e se a API Financial (dev) está no ar.';
        }
      });
  }

  /** Aceita Id numérico (ex.: 42) ou ContractNumber (ex.: B2B-2026-804714). */
  private resolveB2bContractId(raw: string): Observable<{ id: number; contractNumber: string }> {
    const trimmed = raw.trim();
    if (/^\d+$/.test(trimmed)) {
      const id = Number(trimmed);
      return this.contractsService.getB2bContractById(id).pipe(
        map(c => ({
          id: c.id,
          contractNumber: c.contractNumber || String(c.id)
        })),
        catchError(() =>
          throwError(() => new Error(`Contrato B2B Id ${id} não encontrado.`))
        )
      );
    }

    const needle = trimmed.toUpperCase();
    // Busca nas primeiras páginas (API listar não filtra por número).
    return this.contractsService.listB2bContracts(1, 100).pipe(
      switchMap(first => {
        const found = (first.data || []).find(
          c => (c.contractNumber || '').trim().toUpperCase() === needle
        );
        if (found?.id) {
          return of({
            id: found.id,
            contractNumber: found.contractNumber || String(found.id)
          });
        }
        const totalPages = Math.min(first.pagination?.totalPages || 1, 5);
        if (totalPages <= 1) {
          return throwError(
            () =>
              new Error(
                `Contrato "${trimmed}" não encontrado. Use o Id numérico de B2BContracts.`
              )
          );
        }
        return this.contractsService.listB2bContracts(2, 100).pipe(
          map(second => {
            const match = (second.data || []).find(
              c => (c.contractNumber || '').trim().toUpperCase() === needle
            );
            if (!match?.id) {
              throw new Error(
                `Contrato "${trimmed}" não encontrado. Use o Id numérico de B2BContracts.`
              );
            }
            return {
              id: match.id,
              contractNumber: match.contractNumber || String(match.id)
            };
          })
        );
      }),
      catchError(err =>
        throwError(
          () =>
            err instanceof Error
              ? err
              : new Error('Não foi possível buscar o contrato B2B.')
        )
      )
    );
  }

  transitionB2bStatus(): void {
    if (!this.b2bCase || !this.b2bStatusTarget) return;
    this.b2bCaseBusy = true;
    this.b2bCaseError = null;
    const actor =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      'Sistema';
    this.b2bCases
      .transitionStatus(this.b2bCase.id, this.b2bStatusTarget, actor, undefined, this.b2bCase.b2BContractId)
      .subscribe({
        next: c => {
          this.b2bCase = c;
          this.b2bStatusTarget = '';
          this.b2bCaseBusy = false;
          this.errorService.showSuccessMessage('Status do chamado atualizado.');
          this.load();
        },
        error: err => {
          this.b2bCaseBusy = false;
          this.b2bCaseError =
            this.errorService.formatHttpErrorForDisplay(err) || 'Erro ao atualizar status.';
        }
      });
  }

  sendB2bComment(): void {
    const text = (this.b2bCommentDraft || '').trim();
    if (!this.b2bCase || !text) return;
    this.b2bCaseBusy = true;
    this.b2bCaseError = null;
    const actor =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      'Sistema';
    this.b2bCases
      .postComment(this.b2bCase.id, text, {
        interno: this.b2bCommentInternal,
        actorName: actor,
        contractId: this.b2bCase.b2BContractId
      })
      .subscribe({
        next: c => {
          this.b2bComments = [...this.b2bComments, c];
          this.b2bCommentDraft = '';
          this.b2bCaseBusy = false;
        },
        error: err => {
          this.b2bCaseBusy = false;
          this.b2bCaseError =
            this.errorService.formatHttpErrorForDisplay(err) || 'Erro ao enviar mensagem.';
        }
      });
  }

  loadIndication(): void {
    if (!this.id) return;
    this.service.obterIndicacao(this.id).subscribe({
      next: dates => {
        this.indicationLimitAt = dates?.indicationLimitAt ?? null;
        this.indicatedAt = dates?.indicatedAt ?? this.indicatedAt;
        this.rebuildDetailSections();
      },
      error: () => {
        this.indicationLimitAt = null;
      }
    });

    const indicationId = this.getDriverIndicationId(this.fine);
    const indication$ = indicationId
      ? this.driverIndicationService.tryGetById(indicationId)
      : this.driverIndicationService.tryGetByTrafficFineId(this.id);
    indication$.subscribe(indication => {
      this.indicatedDriver = indication;
      if (!this.indicatedAt && indication?.['createdAt']) {
        this.indicatedAt = String(indication['createdAt']);
      }
      this.rebuildDetailSections();
    });
  }

  loadHistories(): void {
    if (!this.id) {
      return;
    }
    this.historyLoading = true;
    this.historyError = null;
    this.service.getHistories(this.id).subscribe({
      next: items => {
        this.history = items
          .map(dto => this.mapHistoryDto(dto))
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        this.historyLoading = false;
      },
      error: err => {
        this.history = [];
        this.historyLoading = false;
        this.historyError =
          this.errorService.formatHttpErrorForDisplay(err) || 'Erro ao carregar histórico da multa.';
      }
    });
  }

  goBack(): void {
    if (this.b2bContractIdContext) {
      this.router.navigate(['/b2b/contracts', this.b2bContractIdContext, 'multas']);
      return;
    }
    this.router.navigate(['/financial/traffic-fines']);
  }

  canGenerateCharge(): boolean {
    return this.getOriginalAmount() > 0 && this.getAccountsReceivableId() == null;
  }

  hasExistingCharge(): boolean {
    return this.getAccountsReceivableId() != null;
  }

  getAccountsReceivableId(): number | null {
    return this.service.resolveAccountsReceivableId(this.fine);
  }

  canConfirmCharge(): boolean {
    return (
      this.canGenerateCharge() &&
      this.getCustomerId() != null &&
      !this.chargeModalLoading &&
      !this.chargeModalSubmitting
    );
  }

  openChargeModal(): void {
    if (!this.fine || !this.canGenerateCharge()) {
      if (this.hasExistingCharge()) {
        this.errorService.showErrorMessage('Esta multa já possui cobrança gerada.');
      }
      return;
    }

    this.chargeModalError = null;
    this.selectedChargeMultiplier = 2;
    this.driverInfo = null;
    this.showChargeModal = true;
    this.chargeModalLoading = true;

    const fineId = this.id;
    const resolvedIndicationId = this.resolveDriverIndicationId(this.fine, this.listRowSnapshot);

    const indication$ = resolvedIndicationId
      ? this.driverIndicationService.tryGetById(resolvedIndicationId)
      : fineId
        ? this.driverIndicationService.tryGetByTrafficFineId(fineId)
        : of(null);

    indication$.subscribe({
      next: indication => {
        this.driverInfo = this.buildDriverInfo(this.fine, indication);
        this.chargeModalLoading = false;
      },
      error: () => {
        this.driverInfo = this.buildDriverInfo(this.fine, null);
        this.chargeModalLoading = false;
      }
    });
  }

  closeChargeModal(): void {
    this.showChargeModal = false;
    this.chargeModalLoading = false;
    this.chargeModalSubmitting = false;
    this.chargeModalError = null;
    this.selectedChargeMultiplier = 2;
    this.driverInfo = null;
  }

  selectChargeMultiplier(multiplier: TrafficFineChargeMultiplier): void {
    if (this.chargeModalSubmitting) {
      return;
    }
    this.selectedChargeMultiplier = multiplier;
  }

  getOriginalAmount(): number {
    const raw = this.pickRaw(this.fine, 'originalAmount', 'OriginalAmount', 'amount', 'Amount');
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  /**
   * Valor explicitamente definido para cobrança ao cliente.
   * NÃO usa adjustedAmount (desconto DETRAN/órgão) — isso não é o que cobramos.
   */
  getFinalAmountToClient(): number | null {
    const raw = this.pickRaw(this.fine, 'finalAmountToClient', 'FinalAmountToClient');
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /** Desconto/ajuste do órgão (ex.: 80% à vista DETRAN) — distinto do valor ao cliente. */
  getOrganAdjustedAmount(): number | null {
    const raw = this.pickRaw(this.fine, 'adjustedAmount', 'AdjustedAmount');
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * À vista: valor da infração (original).
   * Se FinalAmountToClient estiver gravado igual ao desconto do órgão (bug antigo), ignora e usa o original.
   */
  getDisplayClientAmount(): number {
    const final = this.getFinalAmountToClient();
    const organ = this.getOrganAdjustedAmount();
    const original = this.getOriginalAmount();
    if (final != null && organ != null && organ < original && Math.abs(final - organ) < 0.015) {
      return original;
    }
    return final ?? original;
  }

  syncClientAmountDraft(): void {
    const displayed = this.getDisplayClientAmount();
    this.clientAmountDraft = displayed > 0 ? displayed : null;
  }

  useInfractionAmountAsClientCharge(): void {
    const original = this.getOriginalAmount();
    if (original > 0) {
      this.clientAmountDraft = original;
    }
  }

  saveClientAmount(): void {
    if (!this.id || this.clientAmountBusy) return;
    const amount = Number(this.clientAmountDraft);
    if (!Number.isFinite(amount) || amount <= 0) {
      this.errorService.showErrorMessage('Informe um valor cobrado do cliente válido.');
      return;
    }
    this.clientAmountBusy = true;
    const userName =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      'Sistema';
    this.service
      .update(this.id, {
        FinalAmountToClient: amount,
        finalAmountToClient: amount,
        UserName: userName
      })
      .subscribe({
        next: () => {
          this.clientAmountBusy = false;
          this.errorService.showSuccessMessage('Valor cobrado do cliente atualizado.');
          this.load();
        },
        error: err => {
          this.clientAmountBusy = false;
          this.errorService.showErrorMessage(
            this.errorService.formatHttpErrorForDisplay(err) || 'Falha ao salvar valor do cliente.'
          );
        }
      });
  }

  getChargePreview(multiplier: TrafficFineChargeMultiplier): number {
    return calculateTrafficFineChargeAmount(this.getOriginalAmount(), multiplier);
  }

  getSelectedChargeAmount(): number {
    return this.getChargePreview(this.selectedChargeMultiplier);
  }

  getChargeOptionLabel(multiplier: TrafficFineChargeMultiplier): string {
    return this.chargeOptions.find(option => option.multiplier === multiplier)?.label ?? '';
  }

  hasDriverInfo(): boolean {
    return !!this.driverInfo?.name?.trim();
  }

  confirmGenerateCharge(): void {
    if (this.hasExistingCharge()) {
      this.chargeModalError = 'Esta multa já possui cobrança gerada.';
      return;
    }

    if (!this.fine || !this.canConfirmCharge()) {
      if (!this.getCustomerId()) {
        this.chargeModalError =
          'Esta multa não possui cliente vinculado. Vincule um cliente na listagem antes de gerar a cobrança.';
      }
      return;
    }

    if (!this.id) {
      this.chargeModalError = 'ID da multa não informado.';
      return;
    }

    const customerId = this.getCustomerId()!;
    const totalAmount = this.getSelectedChargeAmount();
    if (totalAmount <= 0) {
      this.chargeModalError = 'Valor da cobrança inválido.';
      return;
    }

    this.chargeModalSubmitting = true;
    this.chargeModalError = null;

    const optionLabel = this.getChargeOptionLabel(this.selectedChargeMultiplier);
    const ait = this.aitNumber !== '—' ? this.aitNumber : `MULTA-${this.id}`;
    const contractNumber = this.pickRaw(this.fine, 'contractNumber', 'ContractNumber');
    const staffId = this.authService.getStaffIdFromToken();
    const userName =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      'Sistema';
    const issueDate = this.formatDateForInput(new Date());
    const dueDate = this.resolveChargeDueDate();
    const fineId = this.id;

    const payload: AccountsReceivableCreate = {
      documentNumber: ait,
      documentType: 'Multa de Trânsito',
      issueDate,
      dueDate,
      originalAmount: totalAmount,
      amountToReceive: totalAmount,
      status: 'Pendente',
      category: 'Multas',
      customerId,
      ...(contractNumber ? { contractNumber: String(contractNumber) } : {}),
      ...(staffId != null && { issuedByStaffId: staffId }),
      description: `Multa AIT ${ait} - Cobrança ${optionLabel} (+ R$ 25,00)`
    };

    this.accountsReceivableService
      .checkDocumentNumberExists(ait)
      .pipe(
        catchError(() => of({ exists: false })),
        switchMap(({ exists }) => {
          if (exists) {
            return throwError(() => new Error('Já existe conta a receber com este número de documento (AIT).'));
          }
          return this.accountsReceivableService.createAccountsReceivable(payload);
        }),
        switchMap(created => {
          const accountsReceivableId = created?.id;
          if (!accountsReceivableId) {
            return throwError(() => new Error('Conta a receber criada sem ID retornado pela API.'));
          }
          return this.service
            .update(fineId, {
              AccountsReceivableId: accountsReceivableId,
              FinalAmountToClient: totalAmount,
              finalAmountToClient: totalAmount,
              UserName: userName
            })
            .pipe(
              map(() => created),
              catchError(err => {
                this.load();
                const detail =
                  this.errorService.formatHttpErrorForDisplay(err) ||
                  'Conta a receber criada, mas falhou ao vincular à multa.';
                return throwError(() => new Error(detail));
              })
            );
        })
      )
      .subscribe({
        next: () => {
          this.chargeModalSubmitting = false;
          this.closeChargeModal();
          this.errorService.showSuccessMessage('Conta a receber gerada e vinculada à multa com sucesso.');
          this.load();
        },
        error: err => {
          this.chargeModalSubmitting = false;
          this.chargeModalError = err?.message || 'Erro ao gerar cobrança.';
        }
      });
  }

  get aitNumber(): string {
    return this.pick(this.fine, 'aipNumber', 'AIPNumber') || '—';
  }

  get infractionInfo(): TrafficFineInfractionInfo {
    return this.service.getInfractionInfo(this.fine);
  }

  get infractionStatus(): string {
    return this.formatInfractionStatus(this.infractionInfo.infractionStatus);
  }

  get paymentStatus(): string {
    return this.pick(this.fine, 'paymentStatus', 'PaymentStatus') || '—';
  }

  rebuildDetailSections(): void {
    if (!this.fine) {
      this.detailSections = [];
      return;
    }
    const f = this.fine;
    const original = this.getOriginalAmount();
    const client = this.getFinalAmountToClient();
    let surchargeLabel = '—';
    if (client != null && original > 0) {
      const diff = Math.round((client - original) * 100) / 100;
      surchargeLabel = diff === 0 ? 'Sem acréscimo' : this.formatCurrency(diff);
    }
    const driver = this.buildDriverSection();
    this.detailSections = [
      {
        title: 'Veículo e contrato',
        icon: 'fas fa-car',
        fields: [
          { label: 'Veículo', value: this.pick(f, 'vehicleDescription', 'VehicleDescription') },
          {
            label: 'Placa',
            value: this.pick(f, 'vehiclePlate', 'VehiclePlate', 'plateNumber', 'PlateNumber', 'plate', 'Plate'),
            highlight: true
          },
          { label: 'Contrato', value: this.pick(f, 'contractNumber', 'ContractNumber') },
          { label: 'Cliente', value: this.pick(f, 'customerName', 'CustomerName') }
        ]
      },
      this.buildInfractionSection(),
      ...(driver ? [driver] : []),
      {
        title: 'Financeiro',
        icon: 'fas fa-file-invoice-dollar',
        fields: [
          { label: 'Valor original (multa)', value: this.formatCurrency(original) },
          {
            label: 'Valor cobrado do cliente',
            value: this.formatCurrency(this.getDisplayClientAmount()),
            highlight: true
          },
          { label: 'Acréscimo / diferença', value: surchargeLabel },
          { label: 'Vencimento', value: this.formatDate(this.pickRaw(f, 'dueDate', 'DueDate')) },
          { label: 'Status pagamento', value: this.paymentStatus, highlight: true },
          {
            label: 'Cobrança (conta a receber)',
            value: this.hasExistingCharge() ? 'Gerada' : 'Não gerada',
            highlight: this.hasExistingCharge()
          },
          ...(this.hasExistingCharge()
            ? [{ label: 'Conta a receber', value: String(this.getAccountsReceivableId()) }]
            : [])
        ]
      }
    ];
  }

  /** Monta o card "Infração" a partir dos campos normalizados do serviço. */
  buildInfractionSection(): DetailSection {
    const info = this.infractionInfo;
    return {
      title: 'Infração',
      icon: 'fas fa-gavel',
      fields: [
        { label: 'Descrição', value: this.displayText(info.description) },
        {
          label: 'Data da infração',
          value: this.formatDate(info.infractionDateTime)
        },
        {
          label: 'Prazo para indicar o condutor',
          value: this.formatDate(this.indicationLimitAt)
        },
        {
          label: 'Data da indicação do condutor',
          value: this.formatDate(this.indicatedAt)
        },
        {
          label: 'Cidade / UF',
          value: this.formatCityState(info.city, info.state)
        },
        {
          label: 'Status da infração',
          value: this.formatInfractionStatus(info.infractionStatus),
          highlight: true
        }
      ]
    };
  }

  private buildDriverSection(): DetailSection | null {
    const driver = this.indicatedDriver;
    const name = driver?.driverName || this.pick(this.fine, 'driverName', 'DriverName');
    if (!name || name === '—') return null;
    const pick = (...keys: string[]) => {
      for (const key of keys) {
        const value = driver?.[key];
        if (value != null && String(value).trim()) return String(value).trim();
      }
      return '—';
    };
    return {
      title: 'Condutor indicado',
      icon: 'fas fa-id-card',
      fields: [
        { label: 'Nome', value: name, highlight: true },
        { label: 'CPF', value: pick('documentNumberCPF', 'DocumentNumberCPF') },
        { label: 'RG', value: pick('documentNumberRG', 'DocumentNumberRG') },
        { label: 'Endereço', value: pick('address', 'Address') },
        { label: 'Cidade', value: pick('city', 'City') },
        { label: 'UF', value: pick('state', 'State') },
        { label: 'CEP', value: pick('cep', 'CEP') },
        { label: 'Indicado em', value: this.formatDate(this.indicatedAt || driver?.['createdAt']) }
      ]
    };
  }

  private displayText(value: string): string {
    return value?.trim() ? value.trim() : '—';
  }

  private formatCityState(city: string, state: string): string {
    const c = city?.trim() || '—';
    const s = state?.trim() || '—';
    return `${c} / ${s}`;
  }

  private formatInfractionStatus(status: string): string {
    const s = status?.trim();
    return s ? s.replace(/_/g, ' ') : '—';
  }

  formatDate(val: any): string {
    if (!val) return '—';
    const d = new Date(val);
    return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString('pt-BR');
  }

  formatDateTime(val: string): string {
    if (!val) return '—';
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatCurrency(val: any): string {
    if (val == null || val === '') return '—';
    const n = typeof val === 'number' ? val : parseFloat(String(val).replace(',', '.'));
    if (isNaN(n)) return '—';
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  infractionStatusClass(): string {
    return this.statusBadgeClass(this.infractionStatus);
  }

  paymentStatusClass(): string {
    return this.statusBadgeClass(this.paymentStatus);
  }

  historyStatusClass(status: string): string {
    return this.statusBadgeClass(status);
  }

  historyMarkerColor(tipo: TrafficFineHistoryTipo): string {
    if (tipo === 'sistema') {
      return 'var(--primary-color, #3A4D56)';
    }
    const map: Record<TrafficFineHistoryTipo, string> = {
      registro: 'var(--primary-color, #3A4D56)',
      notificacao: 'var(--primary-light, #5A6D76)',
      indicacao: 'var(--accent-color, #F4A261)',
      financeiro: 'var(--info-color, #17a2b8)',
      pagamento: 'var(--success-color, #28a745)',
      sistema: 'var(--primary-color, #3A4D56)',
      alerta: 'var(--danger-color, #dc3545)'
    };
    return map[tipo] ?? 'var(--secondary-color, #6C7B7F)';
  }

  historyIcon(tipo: TrafficFineHistoryTipo): string {
    const map: Record<TrafficFineHistoryTipo, string> = {
      registro: 'fa-inbox',
      notificacao: 'fa-envelope',
      indicacao: 'fa-user-check',
      financeiro: 'fa-file-invoice-dollar',
      pagamento: 'fa-check-circle',
      sistema: 'fa-cog',
      alerta: 'fa-exclamation-triangle'
    };
    return map[tipo] ?? 'fa-circle';
  }

  trackByHistory(_index: number, event: TrafficFineHistoryEvent): number {
    return event.id;
  }

  pickDisplay(obj: any, ...keys: string[]): string {
    return this.pick(obj, ...keys);
  }

  pickRawDisplay(obj: any, ...keys: string[]): any {
    return this.pickRaw(obj, ...keys);
  }

  private statusBadgeClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (/pago|quitad|liquid|baixad|conclu/i.test(s)) return 'status-badge status-badge--success';
    if (/pend|abert|aguard|venc/i.test(s)) return 'status-badge status-badge--warning';
    if (/cancel|indefer|recus|negad/i.test(s)) return 'status-badge status-badge--danger';
    if (/process|analis|indic/i.test(s)) return 'status-badge status-badge--info';
    return 'status-badge status-badge--neutral';
  }

  private pick(obj: any, ...keys: string[]): string {
    const v = this.pickRaw(obj, ...keys);
    return v != null && v !== '' ? String(v) : '—';
  }

  private pickRaw(obj: any, ...keys: string[]): any {
    if (!obj) return null;
    for (const k of keys) {
      if (obj[k] != null && obj[k] !== '') return obj[k];
    }
    return null;
  }

  private mapHistoryDto(dto: TrafficFineHistoryDto): TrafficFineHistoryEvent {
    const status = (dto.statusTrafficFine ?? (dto as any).StatusTrafficFine ?? '').trim();
    const rawDescription = (dto.description ?? (dto as any).Description ?? '').trim();
    const date = dto.date ?? dto.createdAt ?? (dto as any).Date ?? (dto as any).CreatedAt ?? '';
    const usuario = (dto.userName ?? dto.createdBy ?? (dto as any).UserName ?? (dto as any).CreatedBy ?? '').trim();

    let title: string;
    let detalhes: string | undefined;

    const linked = /vinculad|atribu[ií]d/i.test(rawDescription);
    if (linked && rawDescription) {
      title = rawDescription;
    } else if (status) {
      title = `Status alterado para ${status}`;
      if (rawDescription) {
        detalhes = rawDescription;
      }
    } else if (rawDescription) {
      title = rawDescription;
    } else {
      title = 'Alteração de status';
    }

    return {
      id: dto.id ?? (dto as any).Id ?? 0,
      date: String(date),
      description: title,
      status,
      tipo: this.inferHistoryTipo(rawDescription, status),
      usuario: usuario || undefined,
      detalhes
    };
  }

  private inferHistoryTipo(description: string, status: string): TrafficFineHistoryTipo {
    const text = description.toLowerCase();
    if (/vinculad|atribu[ií]d/.test(text)) return 'notificacao';
    if (/indica/.test(text)) return 'indicacao';
    if (/notifica/.test(text)) return 'notificacao';
    if (/pagamento|pago|quitad/i.test(text)) return 'pagamento';
    if (/financeiro|contas?.?a?.?pagar/i.test(text)) return 'financeiro';
    if (/registr|criad|import/i.test(text)) return 'registro';
    if (/alerta|venc/i.test(text)) return 'alerta';
    return 'sistema';
  }

  getCustomerId(): number | null {
    const raw = this.pickRaw(this.fine, 'customerId', 'CustomerId');
    if (raw == null || raw === '') {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  private resolveChargeDueDate(): string {
    const dueRaw = this.pickRaw(this.fine, 'dueDate', 'DueDate');
    if (dueRaw) {
      const parsed = new Date(dueRaw);
      if (!isNaN(parsed.getTime())) {
        return this.formatDateForInput(parsed);
      }
    }
    const fallback = new Date();
    fallback.setDate(fallback.getDate() + 7);
    return this.formatDateForInput(fallback);
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private resolveDriverIndicationId(...sources: any[]): number | null {
    for (const source of sources) {
      const id = this.getDriverIndicationId(source);
      if (id) {
        return id;
      }
    }
    return null;
  }

  private getDriverIndicationId(fine: any): number | null {
    const raw =
      fine?.driverIndicationId ??
      fine?.DriverIndicationId ??
      fine?.trafficFineDriverIndicationId ??
      fine?.TrafficFineDriverIndicationId;
    if (raw == null || raw === '') {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  private buildDriverInfo(
    fine: any,
    indication?: TrafficFineDriverIndication | null
  ): TrafficFineDriverInfo | null {
    const pickInd = (keys: string[]): string => {
      if (!indication) {
        return '';
      }
      for (const key of keys) {
        const value = indication[key];
        if (value != null && String(value).trim() !== '') {
          return String(value).trim();
        }
      }
      return '';
    };

    const info: TrafficFineDriverInfo = {
      name:
        pickInd(['driverName', 'DriverName']) ||
        this.getItemValue(fine, ['driverName', 'DriverName']),
      cpf:
        pickInd(['documentNumberCPF', 'DocumentNumberCPF']) ||
        this.getItemValue(fine, [
          'documentNumberCPF',
          'DocumentNumberCPF',
          'cpf',
          'CPF',
          'driverCpf',
          'DriverCpf'
        ]),
      rg:
        pickInd(['documentNumberRG', 'DocumentNumberRG']) ||
        this.getItemValue(fine, ['documentNumberRG', 'DocumentNumberRG', 'rg', 'RG', 'driverRg', 'DriverRg']),
      address:
        pickInd(['address', 'Address']) ||
        this.getItemValue(fine, ['address', 'Address', 'driverAddress', 'DriverAddress']),
      city:
        pickInd(['city', 'City']) ||
        this.getItemValue(fine, ['city', 'City', 'driverCity', 'DriverCity']),
      state:
        pickInd(['state', 'State']) ||
        this.getItemValue(fine, ['state', 'State', 'driverState', 'DriverState'])
    };

    return info.name?.trim() ? info : null;
  }

  private getItemValue(item: any, keys: string[]): string {
    for (const key of keys) {
      const value = item?.[key];
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  }
}
